import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { EmptyState } from './states';

/**
 * The empty state of a list, in its two honest forms.
 *
 * A list that is empty because nothing exists yet says so in the list's own words and offers
 * the way to create the first record. A list that is empty because the user narrowed it
 * says THAT instead — "nothing in Articles yet" over a filtered view tells an editor their
 * content is gone — and offers to clear the filters.
 */
export function ListEmpty({
  filtered,
  onClearFilters,
  title,
  body,
  action,
}: {
  /** True when a filter or search is narrowing the list (`useListState().active`). */
  filtered: boolean;
  onClearFilters: () => void;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  if (filtered) {
    return (
      <EmptyState
        eyebrow={t('ui.noMatches')}
        title={t('states.filtered.title')}
        body={t('states.filtered.body')}
        action={
          <Button variant="ghost" size="sm" onClick={onClearFilters}>
            {t('ui.clearAll')}
          </Button>
        }
      />
    );
  }
  return <EmptyState title={title} body={body} action={action} />;
}
